import React, { useEffect, useMemo, useCallback } from "react";
import { Table } from "antd";
import styles from "./BuildQuote.module.scss";
import ConditionalRender from "../../../../reusable-components/ConditionalRender";
import InputField from "../../../../reusable-components/TNInputField";
import { InputfieldTypes } from "../../../../enums/InputFieldAttributesEnums";
import { fieldNames } from "./constant";
import { useMaterials } from "../../../../context/materialListingContext";
import { useTruckDetailContext } from "../../../../context/TruckDetailsContext";
import {
  getColumns,
  headerEstimateTextAreaProps,
  headerTextAreaProps,
} from "./constants";
import { useFormContext } from "react-hook-form";
import CreateJobServices from "../../../../services/createJob.services";
import { individualContent } from "../../../../Types/documentsTypes";
import TNLogo from "../../../../assets/images/TNLogoQuote.png";
import clsx from "clsx";
import { useEstimateTableState } from "../../../../../src/hooks/BuildQuoteHooks/useEstimateTableState";

interface BuildEstimateProps {
  isEdit?: boolean;
  estimateTickedRows?: [];
  setEstimateTickedRows?: () => {};
  estimateFormContent?: individualContent;
  showEstimate?: string;
  salesPerson?: Object;
}
const BuildEstimate = ({
  isEdit,
  estimateTickedRows,
  setEstimateTickedRows,
  estimateFormContent,
  showEstimate,
  salesPerson,
}: BuildEstimateProps) => {
  const { materialTruckList } = useMaterials();
  const { truckDetails } = useTruckDetailContext();
  const { getValues, setValue } = useFormContext();

  const {
    dataSource,
    visiblePrices,
    handleRowSelectionChange,
    togglePriceVisibility,
  } = useEstimateTableState({
    materialTruckList,
    truckDetails,
    estimateTickedRows,
    setEstimateTickedRows,
    showEstimate,
  });

  useEffect(() => {
    if (estimateFormContent) {
      setValue(
        "estimateAggregateSupply1",
        estimateFormContent?.mainDescription,
      );
      setValue("estimateAggregateSupply2", estimateFormContent?.truckingTerms);
      setValue("estimatepaymentTerms", estimateFormContent?.paymentTerms);
      setValue("estimateThankYou", estimateFormContent?.thanksNote);
      setValue(
        fieldNames.ESTIMATE_COMPANY_NAME,
        estimateFormContent?.estimateCompanyName ??
          estimateFormContent?.companyName,
      );
      setValue(
        fieldNames.ESTIMATE_COMPANY_ADDRESS,
        estimateFormContent?.estimateCompanyAddress ??
          estimateFormContent?.companyAddress,
      );
      setValue(
        fieldNames.ESTIMATE_COMPANY_CONTACT,
        estimateFormContent?.estimateCompanyContact ??
          estimateFormContent?.companyContact,
      );
      setValue(
        fieldNames.ESTIMATE_ADDRESS_HEADER,
        estimateFormContent?.estimateAddressTerms,
      );
      setValue(fieldNames.ESTIMATE_HEADER, estimateFormContent?.estimateHeader);
    }
  }, [estimateFormContent, setValue]);


  const columns = useMemo(
    () =>
      getColumns({
        visiblePrices,
        togglePriceVisibility,
        handleSelectVendor: () => {},
      }),
    [visiblePrices, togglePriceVisibility],
  );

  const rowSelection = useMemo(
    () => ({
      onChange: handleRowSelectionChange,
      selectedRowKeys: estimateTickedRows?.map((row) => row._key),
    }),
    [handleRowSelectionChange, estimateTickedRows],
  );

  return (
    <div>
      <div className={styles.LogoAddressContainer}>
        <div>
          <img
            src={TNLogo}
            alt="Twisted Nails"
            className={styles.TwistedNailsLogo}
          />
        </div>
        <div
          className={clsx({
            [styles.AddressContainer]: isEdit,
            [styles.AddressContainer_Content]: !isEdit,
          })}
        >
          <ConditionalRender visible={isEdit}>
            {headerEstimateTextAreaProps.map((field) => (
              <InputField
                textAreaRows={field.textAreaRows}
                showCount
                maxLength={field.maxLength}
                type={field.type}
                name={field.name}
                className={field.className}
              />
            ))}
          </ConditionalRender>
          <ConditionalRender visible={!isEdit}>
            <h5>{getValues(fieldNames.ESTIMATE_COMPANY_NAME)}</h5>
            <h4>{getValues(fieldNames.ESTIMATE_COMPANY_ADDRESS)}</h4>
            <h4>{getValues(fieldNames.ESTIMATE_COMPANY_CONTACT)}</h4>
          </ConditionalRender>
        </div>
      </div>
      <div className={styles.QuoteContainer}>
        <div className={styles.SubHeadingContainer}>
          <ConditionalRender visible={isEdit}>
            <InputField
              textAreaRows={1}
              showCount
              maxLength={30}
              type={InputfieldTypes.TEXT_AREA}
              name={fieldNames.ESTIMATE_HEADER}
            />
          </ConditionalRender>
          <ConditionalRender visible={!isEdit}>
            <h1>{getValues(fieldNames.ESTIMATE_HEADER)}</h1>
          </ConditionalRender>

          <div className={styles.SubHeadingContent}>
            <ConditionalRender
              visible={!isEdit}
              fallback={
                <InputField
                  textAreaRows={10}
                  showCount
                  maxLength={1000}
                  type={InputfieldTypes.TEXT_AREA}
                  name="estimateAggregateSupply1"
                />
              }
            >
              <h5>{getValues("estimateAggregateSupply1")}</h5>
            </ConditionalRender>
            <ConditionalRender visible={isEdit}>
              <InputField
                textAreaRows={1}
                showCount
                maxLength={125}
                type={InputfieldTypes.TEXT_AREA}
                name={fieldNames.ESTIMATE_ADDRESS_HEADER}
              />
            </ConditionalRender>
            <ConditionalRender visible={!isEdit}>
              <div className={styles.AddressHeader}>
                {getValues(fieldNames.ESTIMATE_ADDRESS_HEADER)}
              </div>
            </ConditionalRender>
            <div className={styles.deliveryAddressBlock}>
              {getValues("deliveryAddress")}
              {` (${getValues("deliveryAddressCoordinates")})`}
            </div>
          </div>
        </div>
        <div className={styles.TableContainer}>
          <Table
            columns={columns}
            dataSource={dataSource}
            pagination={false}
            rowSelection={rowSelection}
            rowKey={(record) => record._key}
          />
        </div>
      </div>
      <div className={styles.Content}>
        <ConditionalRender
          visible={!isEdit}
          fallback={
            <InputField
              showCount
              maxLength={1000}
              textAreaRows={10}
              type={InputfieldTypes.TEXT_AREA}
              name="estimateAggregateSupply2"
            />
          }
        >
          <h5>{getValues("estimateAggregateSupply2")}</h5>
        </ConditionalRender>
      </div>
      <div className={styles.BottomContentContainer}>
        <div className={styles.LeftContainer}>
          <div className={styles.Content}>
            <h1>Payment Terms</h1>
            <ConditionalRender
              visible={!isEdit}
              fallback={
                <InputField
                  showCount
                  maxLength={500}
                  textAreaRows={10}
                  type={InputfieldTypes.TEXT_AREA}
                  name="estimatepaymentTerms"
                />
              }
            >
              <h5>{getValues("estimatepaymentTerms")}</h5>
            </ConditionalRender>
          </div>
        </div>
        <div className={styles.RightContainer}>
          <div className={styles.BottomRightContent}>
            <h1>Thank You</h1>
            <ConditionalRender
              visible={!isEdit}
              fallback={
                <InputField
                  showCount
                  maxLength={500}
                  textAreaRows={10}
                  type={InputfieldTypes.TEXT_AREA}
                  name="estimateThankYou"
                />
              }
            >
              <h5>{getValues("estimateThankYou")}</h5>
            </ConditionalRender>
          </div>
          <div className={styles.FillingFields}>
            <div className={styles.SameLine}>
              <div className={styles.UnderLineEmpty}>
                <span>
                  <h3>{salesPerson?.name || ""}</h3>
                </span>
                <div className={styles.UnderLine}></div>
                <h1>Salesperson</h1>
              </div>
              <div className={styles.UnderLineEmpty}>
                <span>
                  <h3>{salesPerson?.phone || ""}</h3>
                </span>
                <div className={styles.UnderLine}></div>
                <h1>Phone</h1>
              </div>
            </div>
            <div className={styles.UnderLineEmpty}>
              <span>
                <h3>{salesPerson?.["email"] || ""}</h3>
              </span>
              <div className={styles.UnderLine}></div>
              <h1>Email</h1>
            </div>
          </div>
          <div
            className={styles.BottomRightContent}
            style={{ padding: "0 0 0 2rem" }}
          >
            <h1>Accounting Department</h1>
            <p>254.644.3714 | remittance@twistednail.com</p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default React.memo(BuildEstimate);
